import { Metamodel } from 'app/model/base/model';
import { ModelMarkerVisitor } from '../../authoring/component/transformations/transformation-expression-builder/model.marker.visitor';
import { ModelEvaluatorVisitor } from '../../authoring/component/transformations/transformation-expression-builder/model.evaluator.visitor';
import { DataFlowExpressionFunctions } from './dataflow-expression-functions';
import {
    convertColumnRefToDataFlowColumn, dataFlowPatternParameter, convertParametersToArguments, dataFlowPatternNameParameter, dataFlowPatternTypeParameter,
    dataFlowPatternStreamParameter, dataFlowPatternOriginParameter, dataFlowPatternPositionParameter, convertCacheSinksToArguments
} from './dataflow-expression-utilities';
import { Expression, FunctionExpression } from './expressionTypes';
import { Antlr4Utils } from '../adapters/dataflow/antlr4-utils';
import { ColumnReference } from 'app/model/dataset/columnReference';
import { AnyDataFlowType, IDataFlowDataType, ArrayDataFlowType, IntegerDataFlowType } from './dataflow-datatype';
import { ExpressionBasedColumn } from 'app/model/dataflow/expression-based.column';
import { ExpressionBasedColumnPattern } from 'app/model/dataflow/expression-based.column.pattern';
import { ExpressionBasedColumnBase } from 'app/model/dataflow/expression-based.column.base';
import { DataFlowColumn, IBasedOnEntityRef } from 'app/model/dataset/dataflowcolumn';
import { Owned } from 'app/model/base/named';
import { DataFlowTypesUtils } from './dataflow-type-utilities';
import { DataFlowExpressionBasedMapColumn } from './dataflow-expression-based-map.column';
import { TemplateExpressionBasedColumnBase } from './template-expression-based.column.base';
import { format } from 'app/shared/utils/string.util';

import { ComplexDataFlowType, ComplexFieldRef } from './dataflow-complex-datatype';
import { DataFlowExpressionUtils } from '../adapters/dataflow/expression.util';
import { DataFlowParameter } from './dataflow-parameter';
import { OwnedSet } from 'shell-models-lib';
import { Transformation } from './transformation';
import { DataFlowSink } from './dataflow-sink';
import { dataFlowHierarchyPathParameter, dataFlowPositionParameter } from 'worker/app-workers/shared/constants';

interface IDataTypeWithException {
    dataType: IDataFlowDataType;
    expressionError: string;
}

export interface IProcessColumnResult {
    baseEntity?: IBasedOnEntityRef;
    column: DataFlowColumn;
}

export class ColumnExpressionProcessor {
    public static calculateExpressionPatternBasedOnType(columns: ExpressionBasedColumnBase[], inputColumnRefs: ColumnReference[]): void {
        ColumnExpressionProcessor._processNewColumns(columns, inputColumnRefs, undefined, true);
    }

    public static processNewColumns(columns: Array<ExpressionBasedColumnBase | DataFlowExpressionBasedMapColumn>,
        inputColumnRefs: IBasedOnEntityRef[], transformation: Transformation): IProcessColumnResult[] {
        return ColumnExpressionProcessor._processNewColumns(columns, inputColumnRefs, transformation);
    }

    public static getColumnNameFromExpressionBasedMapping(mapping: DataFlowExpressionBasedMapColumn, baseEntity: IBasedOnEntityRef, outputColumns?: DataFlowColumn[]): string {
        const inputColumnMatchesConditions = ColumnExpressionProcessor.doesInputColumnMatchConditions(mapping, baseEntity)
            || (outputColumns && !!outputColumns.find((col) => col.name === baseEntity.name));
        if (inputColumnMatchesConditions && mapping.dataFlowExpression && mapping.dataFlowExpression.expression) {
            const regexString = mapping instanceof DataFlowExpressionBasedMapColumn ? mapping.regexString : undefined;
            return ColumnExpressionProcessor._evaluateColumnNameExpressionWithInputColumn(mapping.dataFlowExpression.expression, baseEntity, regexString);
        }

        return '';
    }

    public static getCaptureGroupsFromRegexCondition(regexString: string, stringToMatchOn: string): Map<string, string> {
        const captureGroups = new Map<string, string>();
        if (!regexString || !stringToMatchOn) {
            return captureGroups;
        }

        try {
            const regex = new RegExp(regexString);
            const matches = stringToMatchOn.match(regex);
            if (matches) {
                for (let i = 1; i < matches.length; i++) {
                    captureGroups.set(`$${i}`, matches[i]);
                }
            }
        } catch {
            return captureGroups;
        }

        return captureGroups;
    }

    public static doesInputColumnMatchConditions(mapping: TemplateExpressionBasedColumnBase, baseEntity: IBasedOnEntityRef): boolean {
        const matchesRegex = ColumnExpressionProcessor._doesInputColumnMatchRegex(mapping.regexString, baseEntity);
        const matchesTemplateCondition = mapping.templateCondition && mapping.templateCondition.expression
            && ColumnExpressionProcessor._doesInputColumnMatchTemplateExpression(mapping.templateCondition.expression, baseEntity);

        const bothConditionsGiven = mapping.regexString && mapping.templateCondition && mapping.templateCondition.expression;
        return bothConditionsGiven ? matchesRegex && matchesTemplateCondition : matchesRegex || matchesTemplateCondition;
    }

    private static _doesInputColumnMatchRegex(regexString: string, baseEntity: IBasedOnEntityRef): boolean {
        if (!regexString || !baseEntity) {
            return false;
        }

        try {

            // the regexString must match the entire name, a partial match is not sufficient
            if (regexString[0] !== '^') {
                regexString = '^' + regexString;
            }

            if (regexString[regexString.length - 1] !== '$') {
                regexString = regexString + '$';
            }

            const regex = new RegExp(regexString);
            return regex.test(baseEntity.originalName);
        } catch {
            // Invalid regex
            return false;
        }
    }

    private static _handleDeepHierarchy(levelType: IDataFlowDataType, columnWithExpression: TemplateExpressionBasedColumnBase,
        baseEntityRefs: IBasedOnEntityRef[]): IBasedOnEntityRef[] {
        // For deep hierarchy, we traverse all complex objects to their leaf nodes (visit every primitive subcolumn)
        const baseEntitiesWithHierarchy: IBasedOnEntityRef[] = [];
        const traverse = (level: ComplexDataFlowType, path: string, rootColumn: DataFlowColumn, isHierarchyLevelSpecified: boolean, hierarchyRelativePath: string) => {
            level.fields.forEach((field) => {
                const fieldType = field.dataFlowColumnType instanceof ArrayDataFlowType ? field.dataFlowColumnType.arrayType : field.dataFlowColumnType;
                if (!(fieldType instanceof ComplexDataFlowType)) {
                    baseEntitiesWithHierarchy.push(new ComplexFieldRef(field, path, rootColumn, isHierarchyLevelSpecified, hierarchyRelativePath, true));
                } else {
                    const newPath = (!!path) ? path.concat('.', field.name) : path.concat(field.name);
                    const newHiearchyRelativePath = (!!hierarchyRelativePath) ? hierarchyRelativePath.concat('.', field.name) : hierarchyRelativePath.concat(field.name);
                    traverse(fieldType, newPath, rootColumn, isHierarchyLevelSpecified, newHiearchyRelativePath);
                }
            });
        };

        if (!levelType) {
            // Starting at the top level (i.e no hierarchy level selected)
            baseEntityRefs.forEach((ref) => {
                if (ref.rootEntity && ref.rootEntity.dataFlowColumnType instanceof ComplexDataFlowType) {
                    traverse(ref.rootEntity.dataFlowColumnType, ref.rootEntity.name, (ref.rootEntity instanceof DataFlowColumn) ? ref.rootEntity : undefined, false, '');
                } else {
                    baseEntitiesWithHierarchy.push(ref);
                }
            });
        } else if (levelType instanceof ComplexDataFlowType) {
            // Starting from the hierarchy level selected
            const rootColumn = columnWithExpression.hierarchyLevelExpression.argumentsUsed.dataFlowColumns[0];
            traverse(levelType, columnWithExpression.hierarchyLevelExpression.expression, rootColumn && rootColumn.column.dataFlowColumn, true, '');
        }

        return baseEntitiesWithHierarchy;
    }

    private static _processNewColumns(columns: Array<ExpressionBasedColumnBase | DataFlowExpressionBasedMapColumn>,
        baseEntityRefs: IBasedOnEntityRef[], transformation: Transformation, updateTypeOnly: boolean = false): IProcessColumnResult[] {
        const processedColumns: IProcessColumnResult[] = [];
        columns.forEach((columnWithExpression) => {

            let baseEntitiesWithHierarchy: IBasedOnEntityRef[] = baseEntityRefs;
            if (columnWithExpression instanceof TemplateExpressionBasedColumnBase) {

                const levelType = columnWithExpression.hierarchyLevelExpression.expression
                    ? columnWithExpression.hierarchyLevelExpression.returnType instanceof ArrayDataFlowType
                        ? columnWithExpression.hierarchyLevelExpression.returnType.arrayType : columnWithExpression.hierarchyLevelExpression.returnType
                    : null;

                if (columnWithExpression.deepHierarchy && !columnWithExpression.hierarchyLevelExpression.templateExpression) {
                    baseEntitiesWithHierarchy = this._handleDeepHierarchy(levelType, columnWithExpression, baseEntityRefs);
                } else {
                    baseEntitiesWithHierarchy = levelType
                        ? levelType instanceof ComplexDataFlowType
                            ? levelType.fields.map((field) => {
                                const rootColumn = columnWithExpression.hierarchyLevelExpression.argumentsUsed.dataFlowColumns[0];
                                return new ComplexFieldRef(field, columnWithExpression.hierarchyLevelExpression.expression, rootColumn && rootColumn.column.dataFlowColumn);
                            })
                            : []
                        : baseEntityRefs;
                }
            }

            if (columnWithExpression instanceof ExpressionBasedColumnPattern) {
                const templateColumns: IProcessColumnResult[] = [];

                for (const baseEntity of baseEntitiesWithHierarchy) {
                    const inputColumnMatchesTemplatePredicate = ColumnExpressionProcessor.doesInputColumnMatchConditions(columnWithExpression, baseEntity);

                    if (inputColumnMatchesTemplatePredicate && columnWithExpression.columns) {
                        columnWithExpression.basedColumnDataType = columnWithExpression.basedColumnDataType ?
                            DataFlowTypesUtils.getWiderType(columnWithExpression.basedColumnDataType, baseEntity.entity.dataFlowColumnType)
                            : baseEntity.entity.dataFlowColumnType;

                        if (updateTypeOnly) {
                            continue;
                        }

                        for (const templateColumn of columnWithExpression.columns) {
                            if (!!templateColumn.columnNameExpression && !!templateColumn.columnNameExpression.expression) {
                                const columnName = ColumnExpressionProcessor._evaluateColumnNameExpressionWithInputColumn(templateColumn.columnNameExpression.expression, baseEntity,
                                    columnWithExpression.regexString);

                                const column = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
                                column.name = columnName;
                                column.basedOnArguments.push(baseEntity.entity);
                                templateColumn.columnNameExpression.argumentsUsed.dataFlowParameters.forEach((usedParameter) => {
                                    column.basedOnArguments.push(usedParameter.parameter.dataFlowParameter);
                                });
                                templateColumn.dataFlowExpression.argumentsUsed.dataFlowParameters.forEach((usedParameter) => {
                                    column.basedOnArguments.push(usedParameter.parameter.dataFlowParameter);
                                });
                                column.columnPattern = templateColumn.column;

                                const dataFlowPatternParameterColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
                                dataFlowPatternParameterColumn.name = dataFlowPatternParameter;
                                dataFlowPatternParameterColumn.dataFlowColumnType = DataFlowTypesUtils.cloneType(baseEntity.entity.dataFlowColumnType);
                                const dataFlowPatternParameterColumnRef = new ColumnReference(dataFlowPatternParameterColumn, undefined, undefined, true);

                                const dataFlowMetaColumnRefs: ColumnReference[] = [];

                                const dataFlowPositionParameterColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
                                dataFlowPositionParameterColumn.name = dataFlowPositionParameter;
                                dataFlowPositionParameterColumn.dataFlowColumnType = DataFlowTypesUtils.createType(IntegerDataFlowType.TYPE);
                                dataFlowMetaColumnRefs.push(new ColumnReference(dataFlowPositionParameterColumn, undefined, undefined, true));

                                const dataFlowHierarchicalPathParameterColumn = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
                                dataFlowHierarchicalPathParameterColumn.name = dataFlowHierarchyPathParameter;
                                dataFlowHierarchicalPathParameterColumn.dataFlowColumnType = DataFlowTypesUtils.cloneType(baseEntity.entity.dataFlowColumnType);
                                dataFlowMetaColumnRefs.push(new ColumnReference(dataFlowHierarchicalPathParameterColumn, undefined, undefined, true));

                                if (!!templateColumn.dataFlowExpression && templateColumn.dataFlowExpression.expression) {
                                    const typeWithException = this._getExpressionTypeWithException(templateColumn.dataFlowExpression.expression,
                                        [dataFlowPatternParameterColumnRef, ...dataFlowMetaColumnRefs, ...(baseEntityRefs as ColumnReference[])], transformation.transientColumnRefs,
                                        transformation.dataflow.parameters, transformation.dataflow.cacheSinks);
                                    column.dataFlowColumnType = typeWithException.dataType;
                                    if (!!typeWithException.expressionError) {
                                        templateColumn.dataFlowExpression.addError(
                                            format(stringResources.DataFlowColumnPatternColumnInvalid, baseEntity.name, typeWithException.expressionError));
                                    }
                                } else {
                                    column.dataFlowColumnType = new AnyDataFlowType();
                                }
                                templateColumns.push({ column, baseEntity });

                                templateColumn.column.dataFlowColumnType = templateColumn.column.dataFlowColumnType ?
                                    DataFlowTypesUtils.getWiderType(templateColumn.column.dataFlowColumnType, column.dataFlowColumnType)
                                    : column.dataFlowColumnType;
                            }
                        }
                    }
                }
                processedColumns.push(...templateColumns);
            } else if (columnWithExpression instanceof ExpressionBasedColumn) {
                if (columnWithExpression.dataFlowExpression.returnType) {
                    columnWithExpression.column.dataFlowColumnType = columnWithExpression.expressionReturnType;
                } else if (!columnWithExpression.column.dataFlowColumnType) {
                    console.warn('should not be called');
                    columnWithExpression.column.dataFlowColumnType = columnWithExpression.dataFlowExpression && columnWithExpression.dataFlowExpression.expression ?
                        this._getExpressionType(columnWithExpression.dataFlowExpression.expression, baseEntityRefs as ColumnReference[], transformation.transientColumnRefs,
                            transformation.dataflow.parameters, transformation.dataflow.cacheSinks) : new AnyDataFlowType();
                }
                columnWithExpression.column.nameDependsOnBasedColumn = columnWithExpression.dataFlowExpression.expression === columnWithExpression.column.name;
                if (columnWithExpression.dataFlowExpression) {
                    columnWithExpression.column.basedOnArguments = [];
                    columnWithExpression.dataFlowExpression.argumentsUsed.dataFlowColumns.forEach((usedColumn) => {
                        if (usedColumn.fields) {
                            columnWithExpression.column.basedOnArguments.push(...usedColumn.fields);
                        } else {
                            columnWithExpression.column.basedOnArguments.push(usedColumn.column.dataFlowColumn);
                        }
                    });
                    columnWithExpression.dataFlowExpression.argumentsUsed.dataFlowParameters.forEach((usedParameter) => {
                        columnWithExpression.column.basedOnArguments.push(usedParameter.parameter.dataFlowParameter);
                    });
                }
                processedColumns.push({ column: columnWithExpression.column });
            } else if (columnWithExpression instanceof DataFlowColumn) {
                processedColumns.push({ column: columnWithExpression });
            } else if (columnWithExpression instanceof DataFlowExpressionBasedMapColumn) {
                let streamName: string;
                if (columnWithExpression && columnWithExpression.templateCondition && columnWithExpression.templateCondition.expression) {
                    const tokenStream = Antlr4Utils.getTokenStream(columnWithExpression.templateCondition.expression);
                    const expression = Antlr4Utils.getExpression(tokenStream);
                    if (expression?.children &&
                        expression.children[0] && expression.children[0].getText && expression.children[0].getText() === dataFlowPatternStreamParameter &&
                        expression.children[1] && expression.children[1].getText && expression.children[1].getText() === '==' &&
                        expression.children[2] && expression.children[2].getText && (expression.children[2].getText().startsWith('\'') || expression.children[2].getText().startsWith('"'))) {
                        streamName = DataFlowExpressionUtils.trimSpacesAndAllQuotesInDslString(expression.children[2].getText());
                    }
                }
                const matchingTransform = streamName &&
                    transformation.getAllOwnerTransformationsUntilBlocked().find((upstreamTransform) => upstreamTransform.name === streamName);
                const matchingTransformOutputColumns = matchingTransform && matchingTransform.outputColumns();
                for (const baseEntity of baseEntitiesWithHierarchy) {
                    const columnName = ColumnExpressionProcessor.getColumnNameFromExpressionBasedMapping(columnWithExpression, baseEntity, matchingTransformOutputColumns);
                    if (columnName) {
                        const column = Owned.create<DataFlowColumn>(Metamodel.modelFor(DataFlowColumn), transformation);
                        column.name = columnName;
                        column.basedOnArguments.push(baseEntity.entity);
                        columnWithExpression.dataFlowExpression.argumentsUsed.dataFlowParameters.forEach((usedParameter) => {
                            column.basedOnArguments.push(usedParameter.parameter.dataFlowParameter);
                        });
                        column.dataFlowColumnType = baseEntity.entity.dataFlowColumnType;

                        processedColumns.push({ column, baseEntity });
                    }
                }
            }
        });

        return processedColumns;
    }

    private static _doesInputColumnMatchTemplateExpression(expressionStr: string, baseEntity: IBasedOnEntityRef): boolean {
        const runtimeContext = ColumnExpressionProcessor._getRuntimeContextForTemplateExpression(baseEntity);
        const evaluatedTemplateConditionReturnValue = ColumnExpressionProcessor._evaluateBooleanExpressionReturnValue(expressionStr, runtimeContext);
        return evaluatedTemplateConditionReturnValue === true;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static _getRuntimeContextForTemplateExpression(baseEntity: IBasedOnEntityRef): Map<string, any> {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const runtimeContext = new Map<string, any>();
        runtimeContext.set(dataFlowPatternNameParameter, baseEntity.originalName);
        runtimeContext.set(dataFlowPatternTypeParameter, baseEntity.entity.dataFlowColumnType ? baseEntity.entity.dataFlowColumnType.fullyQualifiedType : '');
        if (baseEntity.owningStreamName) {
            runtimeContext.set(dataFlowPatternStreamParameter, baseEntity.owningStreamName);
        }
        if (baseEntity.owningStreamName) {
            runtimeContext.set(dataFlowPatternOriginParameter, baseEntity.owningStreamName);
        }
        runtimeContext.set(dataFlowPatternPositionParameter, baseEntity.siblingEntities.findIndex((col) => col.name === baseEntity.originalName) + 1); // Position is 1-indexed

        return runtimeContext;
    }

    private static _evaluateColumnNameExpressionWithInputColumn(columnNameExpressionStr: string, baseEntity: IBasedOnEntityRef, regexString?: string): string {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const runtimeContext = new Map<string, any>();
        runtimeContext.set(dataFlowPatternParameter, baseEntity.originalName);
        runtimeContext.set(dataFlowPositionParameter, baseEntity.siblingEntities.findIndex((col) => col.name === baseEntity.originalName) + 1);

        if (baseEntity instanceof ComplexFieldRef && baseEntity.deepHierarchyisApplied) {
            if (baseEntity.isHierarchyLevelSpecified) {
                runtimeContext.set(dataFlowHierarchyPathParameter, baseEntity.hierarchicalName);
            } else {
                runtimeContext.set(dataFlowHierarchyPathParameter, baseEntity.name);
            }
        } else {
            runtimeContext.set(dataFlowHierarchyPathParameter, baseEntity.originalName);
        }

        if (regexString) {
            const captureGroups = ColumnExpressionProcessor.getCaptureGroupsFromRegexCondition(regexString, baseEntity.originalName);
            captureGroups.forEach((value, key) => runtimeContext.set(key, value));
        }

        const evaluatedColumnNameExpression = ColumnExpressionProcessor._evaluateExpression(columnNameExpressionStr, runtimeContext);
        return evaluatedColumnNameExpression ? evaluatedColumnNameExpression.returnValue : '';
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static _evaluateExpression(expressionStr: string, runtimeContext?: Map<string, any>): Expression {
        if (!expressionStr) {
            return undefined;
        }

        // cache hit
        if (this._cachedModelEvaluatorVisitor.has(expressionStr)) {
            const theValue = this._cachedModelEvaluatorVisitor.get(expressionStr);
            const { visitor, expression, lastEvaluatedExpression } = theValue;
            visitor.dataflowRuntimeContext = runtimeContext;
            const newlyEvaluatedExpression = visitor.visit(expression) as (Expression | undefined);
            if (!lastEvaluatedExpression && !!newlyEvaluatedExpression) {
                theValue.lastEvaluatedExpression = newlyEvaluatedExpression;
            }
            return newlyEvaluatedExpression;
        } else {
            const tokenStream = Antlr4Utils.getTokenStream(expressionStr);
            const expression = Antlr4Utils.getExpression(tokenStream);
            const markerVisitor = new ModelEvaluatorVisitor(tokenStream, runtimeContext);
            const lastEvaluatedExpression = markerVisitor.visit(expression) as (Expression | undefined);
            this._cachedModelEvaluatorVisitor.set(expressionStr, { visitor: markerVisitor, expression, lastEvaluatedExpression });
            return lastEvaluatedExpression;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static _evaluateBooleanExpressionReturnValue(expressionStr: string, runtimeContext?: Map<string, any>): any {
        if (!expressionStr) {
            return undefined;
        }

        // cache hit
        if (this._cachedModelEvaluatorVisitor.has(expressionStr)) {
            const theValue = this._cachedModelEvaluatorVisitor.get(expressionStr);
            const { visitor, expression, lastEvaluatedExpression } = theValue;

            if (
                // we gotta historic evaluated expression
                !!lastEvaluatedExpression &&
                // and the just-evaluated expression of the same CST is a function
                lastEvaluatedExpression.functionExpression instanceof FunctionExpression &&
                // and that function cannot be interpreted at client runtime (just like compile time)
                lastEvaluatedExpression.functionExpression.isCompileTimeExecutable === false
            ) {
                return undefined;
            }

            visitor.dataflowRuntimeContext = runtimeContext;
            const newlyEvaluatedExpression = visitor.visit(expression) as (Expression | undefined);
            if (!lastEvaluatedExpression && !!newlyEvaluatedExpression) {
                theValue.lastEvaluatedExpression = newlyEvaluatedExpression;
            }
            return newlyEvaluatedExpression?.returnValue;
        } else {
            const tokenStream = Antlr4Utils.getTokenStream(expressionStr);
            const expression = Antlr4Utils.getExpression(tokenStream);
            const markerVisitor = new ModelEvaluatorVisitor(tokenStream, runtimeContext);
            const lastEvaluatedExpression = markerVisitor.visit(expression) as (Expression | undefined);
            this._cachedModelEvaluatorVisitor.set(expressionStr, { visitor: markerVisitor, expression, lastEvaluatedExpression });
            return lastEvaluatedExpression?.returnValue;
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static _cachedModelEvaluatorVisitor: Map<string, { expression: any, visitor: ModelEvaluatorVisitor, lastEvaluatedExpression?: Expression }> = new Map();


    private static _getExpressionTypeWithException(expressionStr: string, columnRefs: ColumnReference[],
        transientColumnRefs: ColumnReference[], param: OwnedSet<DataFlowParameter>, cacheSinks: OwnedSet<DataFlowSink>): IDataTypeWithException {
        const tokenStream = Antlr4Utils.getTokenStream(expressionStr);
        const expressionContext = Antlr4Utils.getExpression(tokenStream);
        const visitor = new ModelMarkerVisitor(tokenStream, {
            dataFlowColumns: convertColumnRefToDataFlowColumn(columnRefs),
            dataFlowFunctions: DataFlowExpressionFunctions.ALL_INLCUDING_WINDOW_FUNCTIONS,
            dataFlowParameters: convertParametersToArguments(param),
            dataFlowTransientColumns: convertColumnRefToDataFlowColumn(transientColumnRefs),
            dataFlowCacheSinks: convertCacheSinksToArguments(cacheSinks)
        });

        const expression: Expression = visitor.visit(expressionContext);
        return {
            dataType: expression.returnType,
            expressionError: visitor.expressionErrors.length > 0 ? visitor.expressionErrors[0] : undefined
        };
    }

    private static _getExpressionType(expressionStr: string, columnRefs: ColumnReference[], transientColumnRefs: ColumnReference[],
        params: OwnedSet<DataFlowParameter>, cacheSinks: OwnedSet<DataFlowSink>): IDataFlowDataType {
        const expType = ColumnExpressionProcessor._getExpressionTypeWithException(expressionStr, columnRefs, transientColumnRefs, params, cacheSinks);
        return expType.dataType;
    }
}
